import { renderTemplate } from '../../components/utils';
import { Translations } from '../../i18n/i18n';
import { ApiSchedule, ApiSubscriptionOrder, OrderStatus } from '../../types';
import { ValuePiece } from '../Calendar/Calendar';

const getDateMonthDays = (date: Date): number => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const monthsBetween = (from: Date, to: Date): number =>
	(to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());

export const DEFAULT_SHIP_IN_HOURS = 72;

export type DaysOrderKey = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const DAYS_ORDER = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 0: 7 };

export const sortDays = (a: keyof typeof DAYS_ORDER, b: keyof typeof DAYS_ORDER): number =>
	DAYS_ORDER[a] - DAYS_ORDER[b];

export const parseWeeklyDays = (weeklyDays: string): [DaysOrderKey] => JSON.parse(weeklyDays);

export const stringifyWeeklyDays = (weeklyDays: number[]): string => JSON.stringify(weeklyDays);

export const formatCalendarDate = (value: Date | [ValuePiece, ValuePiece]) => {
	const date = new Date(value.toString());
	return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
};

export const addHours = (date: Date, hours: number) => {
	date.setTime(date.getTime() + hours * 60 * 60 * 1000);
	return date;
};

/*
 * The api sends a datetime as `YYYY-MM-DD HH:mm:ss`, which the legacy build's own target - safari
 * 12, named in vite.config.ts - does not parse with `new Date`. There it came out Invalid Date, so
 * every created order read as historical and its date rendered as `NaN-NaN-NaN`.
 */
export const parseApiDateTime = (value: string): Date => new Date(value.replace(' ', 'T'));

type CreatedOrder = { order: number; dispatch: Date; editable: boolean };

/*
 * The statuses that mean an order is not going to happen: denied, written off, cancelled either way
 * round, and deleted. An order in any other state is on its way as far as the card is concerned -
 * an unknown or new status must not make it disappear - and `undefined` is an api older than
 * kg-api #85, which answered without a status at all.
 */
const NOT_HAPPENING: OrderStatus[] = [0, 9, 18, 19, 99];

export const orderIsHappening = (status?: OrderStatus | null): boolean =>
	status === undefined || status === null || !NOT_HAPPENING.includes(status);

const isHappening = ({ status }: ApiSubscriptionOrder) => orderIsHappening(status);

// addHours mutates the date it is handed, so each order gets a date of its own
const dispatchOf = (order: ApiSubscriptionOrder) =>
	addHours(parseApiDateTime(order.dateCreated), Number(order.shipInHours));

/*
 * The orders a schedule has created that have not left the warehouse yet, soonest first. An order
 * is created `dateCreated` and ships `shipInHours` later, which is also the last moment it can be
 * changed - so a dispatch still ahead is an order the customer can still act on.
 *
 * There can be more than one: a daily schedule creates an order a day while they stay editable for
 * two, so two of them are in flight at once. They are sorted here rather than trusted in the order
 * they arrived, since kg-api's `getBySubscription` selects them with no `ORDER BY`.
 */
export const upcomingOrdersOf = (schedule?: ApiSchedule): CreatedOrder[] =>
	(schedule?.orders ?? [])
		.filter((order) => isHappening(order) && dispatchOf(order).getTime() > Date.now())
		// an api that was not asked says null, and the card offers no change it cannot be sure of
		.map((order) => ({ order: order.order, dispatch: dispatchOf(order), editable: order.editable === true }))
		.sort((one, other) => one.dispatch.getTime() - other.dispatch.getTime());

/*
 * The last thing that actually happened, for a refill with nothing on its way. Only orders that
 * have left the warehouse: one that is still ahead is either on its way - and shown as that - or
 * cancelled, and a cancelled order is not the last thing that happened to anybody.
 */
export const lastOrderOf = (schedule?: ApiSchedule): CreatedOrder | undefined => {
	const dispatched = (schedule?.orders ?? []).filter((order) => dispatchOf(order).getTime() <= Date.now());

	if (!dispatched.length) return undefined;

	const latest = dispatched.reduce((newest, order) =>
		parseApiDateTime(order.dateCreated) > parseApiDateTime(newest.dateCreated) ? order : newest
	);

	return { order: latest.order, dispatch: dispatchOf(latest), editable: latest.editable === true };
};

const checkNumberEnding = (value: number): number => {
	if ([11, 12].includes(value % 100)) {
		return 0;
	}

	if (value > 20 && [1, 2].includes(value % 10)) {
		return 1;
	}

	return 0;
};

const getRecurrenceValueText = (recurrenceValue: number, t: Translations['subscriptions']): string => {
	if (recurrenceValue < 4) {
		return t.schedule.recurrence[recurrenceValue.toString() as keyof typeof t.schedule.recurrence];
	}

	return `${t.schedule.each} ${recurrenceValue}${t.schedule.counterPunctuation[checkNumberEnding(recurrenceValue)]}`;
};

const translateDayOrder = (date: number, t: Translations['subscriptions']): string => {
	if (date < 4) {
		return Object.values(t.schedule.counters).find((c) => c.id === date)?.text || '';
	}

	return `${date}${t.schedule.counterPunctuation[checkNumberEnding(date)]}`;
};

const translateWeekday = (weekday: number, t: Translations['subscriptions']): string =>
	Object.values(t.schedule.weekdays).find((day) => day.id === weekday)?.text || '';

const translateWeeklyDays = (weeklyDays: DaysOrderKey[], t: Translations['subscriptions'], locale: string): string =>
	new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
		weeklyDays.sort(sortDays).map((weekDay) => translateWeekday(weekDay, t) + t.schedule.pluralSuffix)
	);

export const translateCounter = (counter: number, t: Translations['subscriptions']): string =>
	Object.values(t.schedule.counters).find((c) => c.id === counter)?.text || '';

export const findNextValidDay = () => {
	let searchDate = new Date();
	let iterations = 0;
	const iterationsLimit = 1000;
	const twoDaysAhead = new Date(new Date().setHours(new Date().getHours() + DEFAULT_SHIP_IN_HOURS));

	while (iterations < iterationsLimit) {
		if (!isWeekend(searchDate) && !isSwedishHoliday(searchDate) && twoDaysAhead < searchDate) {
			return searchDate;
		}
		searchDate.setDate(searchDate.getDate() + 1);
	}

	return new Date();
};

export const getScheduleSuggestions = (startDate: string, listId: number): ApiSchedule[] => {
	const weekDay = new Date(startDate).getDay();
	const date = new Date(startDate).getDate();

	return [
		{
			id: 5,
			productListId: listId,
			recurrencePattern: 'daily',
			recurrenceValue: 2,
			weeklyDays: '[]',
			startDate,
			orders: [],
		},
		{
			id: 1,
			productListId: listId,
			recurrencePattern: 'weekly',
			recurrenceValue: 1,
			weeklyDays: `[${weekDay}]`,
			startDate,
			orders: [],
		},
		{
			id: 3,
			productListId: listId,
			recurrencePattern: 'weekly',
			recurrenceValue: 2,
			weeklyDays: `[${weekDay}]`,
			startDate,
			orders: [],
		},
		{
			id: 2,
			productListId: listId,
			recurrencePattern: 'monthly',
			recurrenceValue: 1,
			monthlyWeekday: weekDay,
			monthlyWeekdayCounter: getProgressionPos(new Date(startDate)),
			monthlyInterval: 'weekday',
			weeklyDays: '[]',
			startDate,
			orders: [],
		},
		{
			id: 4,
			productListId: listId,
			recurrencePattern: 'monthly',
			recurrenceValue: 2,
			monthlyDay: date,
			monthlyInterval: 'date',
			weeklyDays: '[]',
			startDate,
			orders: [],
		},
	];
};

/**
 * The counter to suggest for a date the customer picked: which occurrence of its own weekday it is,
 * or -1 when no later one fits in the month. A date that is both the fourth and the last monday is
 * reported as the last, since that is the one that exists in every month.
 */
export const getProgressionPos = (date: Date): number => {
	const maxDays = getDateMonthDays(date);
	let day = date.getDate();

	if (day > maxDays - 7) {
		return -1;
	}
	let pos = 1;

	while (day > 7) {
		pos++;
		day = day - 7;
	}

	return pos;
};

/** which occurrence of its own weekday a date is: the 8th of a month is the second of them */
const weekdayOccurrence = (date: Date): number => Math.ceil(date.getDate() / 7);

const isLastWeekdayOfMonth = (date: Date): boolean => date.getDate() + 7 > getDateMonthDays(date);

/**
 * Whether a date is the one a `monthlyWeekdayCounter` asks for.
 *
 * This cannot be `getProgressionPos(date) === counter`, which is what it used to be. That function
 * answers "which counter would I suggest for this date" and reports anything in the month's last
 * seven days as the last one - and a fourth weekday falls on the 22nd to the 28th, which is inside
 * those seven days in every month of 30 days or fewer, and in a 31-day month from the 25th. So a
 * schedule set to the fourth monday matched nothing, the search ran past its own window, and the
 * date it happened to stop on was returned as the next order: 2 March 2026 for a schedule that
 * meant 23 February. Asking the two questions separately is the whole fix.
 */
export const matchesWeekdayCounter = (date: Date, counter: number): boolean =>
	counter === -1 ? isLastWeekdayOfMonth(date) : weekdayOccurrence(date) === counter;

export const formatScheduleText = (schedule: ApiSchedule, locale: string, t: Translations['subscriptions']): string => {
	if (!schedule || !schedule.startDate || !schedule.recurrenceValue || !schedule.recurrencePattern) return '';

	const {
		recurrenceValue,
		recurrencePattern,
		monthlyWeekdayCounter,
		monthlyWeekday,
		weeklyDays,
		monthlyDay,
		monthlyInterval,
	} = schedule;

	const recurrenceValueText = getRecurrenceValueText(recurrenceValue, t);
	const parsedWeeklyDays = parseWeeklyDays(weeklyDays);

	if (recurrencePattern === 'daily') {
		return renderTemplate(t.schedule.daily, {
			recurrenceValue: recurrenceValueText,
		});
	} else if (recurrencePattern === 'weekly' && Boolean(parsedWeeklyDays.length)) {
		const weekdayLong = new Date(schedule.startDate).toLocaleDateString(locale, { weekday: 'long' });

		return renderTemplate(t.schedule.weekly, {
			recurrenceValue: recurrenceValueText,
			weekday: parsedWeeklyDays ? translateWeeklyDays(parsedWeeklyDays, t, locale) : weekdayLong,
		});
	} else if (schedule.recurrencePattern === 'monthly') {
		if (monthlyInterval === 'weekday' && monthlyWeekdayCounter && monthlyWeekday !== undefined) {
			return renderTemplate(t.schedule.monthlyWithWeekday, {
				recurrenceValue: recurrenceValueText,
				weekdayCounter: translateCounter(monthlyWeekdayCounter, t),
				weekday: translateWeekday(monthlyWeekday, t),
				suffix: t.schedule.definiteFormSuffix,
			});
		}

		if (monthlyInterval === 'date' && monthlyDay) {
			return renderTemplate(t.schedule.monthly, {
				recurrenceValue: recurrenceValueText,
				date: translateDayOrder(monthlyDay, t),
			});
		}
	}

	return '';
};

const WEEKENDS = [0, 6];

const addDays = (date: Date, days: number): Date =>
	new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** the first `weekday` falling on or after `from`, which is how the floating holidays are defined */
const weekdayOnOrAfter = (from: Date, weekday: number): Date => addDays(from, (weekday - from.getDay() + 7) % 7);

/** easter sunday by the anonymous gregorian algorithm; six of the holidays below are counted off it */
const easterSunday = (year: number): Date => {
	const a = year % 19;
	const b = Math.floor(year / 100);
	const c = year % 100;
	const d = Math.floor(b / 4);
	const e = b % 4;
	const f = Math.floor((b + 8) / 25);
	const g = Math.floor((b - f + 1) / 3);
	const h = (19 * a + b - d - g + 15) % 30;
	const i = Math.floor(c / 4);
	const k = c % 4;
	const l = (32 + 2 * e + 2 * i - h - k) % 7;
	const m = Math.floor((a + 11 * h + 22 * l) / 451);
	const month = Math.floor((h + l - 7 * m + 114) / 31);
	const day = ((h + l - 7 * m + 114) % 31) + 1;

	return new Date(year, month - 1, day);
};

const monthAndDay = (date: Date): string =>
	`${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

const holidaysByYear = new Map<number, Set<string>>();

/**
 * The days the warehouse does not send anything.
 *
 * These used to be a list of fixed day-and-month pairs, which held 2024's easter: maundy thursday on
 * 28 March, ascension on 9 May, whitsun on 19 May. Six of them move with easter and three more are a
 * floating friday or saturday, so the list was wrong in every other year - blocking ordinary working
 * days and letting the real holidays through. They are counted off easter instead.
 *
 * Midsummer's eve and new year's eve are in it although they are not public holidays: nothing leaves
 * the warehouse on either. Midsummer's day and all saints' day are always saturdays and so already
 * weekends, but they are kept for the sake of saying what the list is.
 */
const swedishHolidays = (year: number): Set<string> => {
	const known = holidaysByYear.get(year);
	if (known) return known;

	const easter = easterSunday(year);

	const days = [
		new Date(year, 0, 1), // nyårsdagen
		new Date(year, 0, 6), // trettondedag jul
		addDays(easter, -3), // skärtorsdag
		addDays(easter, -2), // långfredag
		easter, // påskdagen
		addDays(easter, 1), // annandag påsk
		new Date(year, 4, 1), // första maj
		addDays(easter, 39), // kristi himmelsfärdsdag
		addDays(easter, 49), // pingstdagen
		new Date(year, 5, 6), // nationaldagen
		weekdayOnOrAfter(new Date(year, 5, 19), 5), // midsommarafton, the friday 19-25 june
		weekdayOnOrAfter(new Date(year, 5, 20), 6), // midsommardagen, the saturday 20-26 june
		weekdayOnOrAfter(new Date(year, 9, 31), 6), // alla helgons dag, the saturday 31 oct - 6 nov
		new Date(year, 11, 24), // julafton
		new Date(year, 11, 25), // juldagen
		new Date(year, 11, 26), // annandag jul
		new Date(year, 11, 31), // nyårsafton
	];

	const holidays = new Set(days.map(monthAndDay));
	holidaysByYear.set(year, holidays);

	return holidays;
};

export const isSwedishHoliday = (date: Date): boolean => swedishHolidays(date.getFullYear()).has(monthAndDay(date));

export const isWeekend = (date: Date): boolean => WEEKENDS.includes(date.getDay());

/**
 * A weekday number that falls on a weekend.
 *
 * The weekly picker offered saturday and sunday although the start-date calendar blocks both and
 * nothing leaves the warehouse then, so a customer could set "every saturday" and be told their
 * order would go on a day it could not.
 */
export const isWeekendDay = (weekday: number): boolean => WEEKENDS.includes(weekday);

const calculateNextOrderDaily = (startDate: string, recurrenceValue: number): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	const searchDate = new Date(start.valueOf());

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	while (today.getTime() > searchDate.getTime() && iterations < iterationLimit) {
		searchDate.setDate(searchDate.getDate() + recurrenceValue);

		iterations++;
	}

	return searchDate;
};

const calculateNextOrderWeekly = (startDate: string, weeklyDays: number[], recurrenceValue: number): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	const firstDate = today.getTime() > start.getTime() ? today : start;

	const searchDate = new Date(start.getTime());
	const endDate = new Date(new Date(firstDate.getTime()).setDate(1 + 7 * recurrenceValue + firstDate.getDate()));
	let found = false;

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	while (endDate.getTime() >= searchDate.getTime() && iterations < iterationLimit) {
		if (weeklyDays.includes(searchDate.getDay())) {
			if (today.getTime() <= searchDate.getTime() && start.getTime() <= searchDate.getTime()) break;

			found = true;
		}

		if (searchDate.getDay() === 0 && found) {
			searchDate.setDate(1 + 7 * (recurrenceValue - 1) + searchDate.getDate());
		} else {
			searchDate.setDate(searchDate.getDate() + 1);
		}

		iterations++;
	}

	return new Date(searchDate);
};

const calculateNextOrderMonthly = (schedule: ApiSchedule): Date | null => {
	const { recurrenceValue, monthlyWeekdayCounter, monthlyWeekday, monthlyDay, startDate, monthlyInterval } = schedule;

	if (monthlyInterval === 'weekday' && monthlyWeekdayCounter && monthlyWeekday !== undefined) {
		return calculateNextOrderMonthlyWeekday(startDate, monthlyWeekday, monthlyWeekdayCounter, recurrenceValue);
	}

	if (monthlyInterval === 'date' && monthlyDay) {
		return calculateNextOrderMonthlyDate(startDate, monthlyDay, recurrenceValue);
	}

	return null;
};

const calculateNextOrderMonthlyWeekday = (
	startDate: string,
	weekday: number,
	counter: number,
	recurrenceValue: number
): Date | null => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	let firstDate = today.getTime() > start.getTime() ? today : start;
	/*
	 * A month more than the recurrence. The scan starts at the start date, which can be past this
	 * month's occurrence, and then jumps a whole recurrence to the 1st - so the answer can sit just
	 * beyond a window of exactly one recurrence. A schedule for the first monday starting thursday
	 * 4 January 2024 wants 5 February, one day past it.
	 */
	let endDate = new Date(new Date(firstDate.getTime()).setMonth(firstDate.getMonth() + recurrenceValue + 1));

	/*
	 * Start the scan at the first recurrence month that can still hold an answer.
	 *
	 * It used to begin at the start date and walk a day at a time, so a schedule more than about
	 * thirty-three months old spent its whole iteration budget getting to today and found nothing -
	 * which, now that nothing means null, would have left an old subscription with no next order at
	 * all rather than a wrong one.
	 */
	const periodsElapsed = Math.max(0, Math.ceil(monthsBetween(start, firstDate) / recurrenceValue));
	const searchDate = new Date(start.getTime());

	if (periodsElapsed > 0) {
		searchDate.setDate(1);
		searchDate.setMonth(start.getMonth() + periodsElapsed * recurrenceValue);
	}

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	// If searchDate is before today, keep adding months by recurrenceValue until searchDate is after today and searchDate's
	// day is equal to weekday from parameters
	while (endDate.getTime() >= searchDate.getTime() && iterations < iterationLimit) {
		const maxDays = getDateMonthDays(searchDate);

		if (
			searchDate.getDay() === weekday &&
			matchesWeekdayCounter(searchDate, counter) &&
			searchDate.getTime() >= today.getTime()
		) {
			return searchDate;
		}

		if (maxDays === searchDate.getDate()) {
			searchDate.setDate(1);
			searchDate.setMonth(searchDate.getMonth() + recurrenceValue);
		} else {
			searchDate.setDate(searchDate.getDate() + 1);
		}

		iterations++;
	}

	// nothing in the window matched, and the date the search stopped on is not an answer
	return null;
};

const calculateNextOrderMonthlyDate = (startDate: string, monthlyDay: number, recurrenceValue: number): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	// Set searchDate to the startDate and set searchDate's day to MonthlyDay from parameters or last of month if it's greater than monthlyDay
	let maxDays = getDateMonthDays(start);
	const searchDate = new Date(new Date(start.getTime()).setDate(monthlyDay > maxDays ? maxDays : monthlyDay));

	// If searchDate is on or after startDate and today, return searchDate
	if (searchDate.getTime() >= start.getTime() && searchDate.getTime() >= today.getTime()) return searchDate;

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	// While searchDate is before today or startDate, keep adding months by recurrenceValue
	while (
		(today.getTime() > searchDate.getTime() || start.getTime() > searchDate.getTime()) &&
		iterations < iterationLimit
	) {
		searchDate.setDate(1);
		searchDate.setMonth(searchDate.getMonth() + recurrenceValue);
		const maxDays = getDateMonthDays(searchDate);
		searchDate.setDate(monthlyDay > maxDays ? maxDays : monthlyDay);

		iterations++;
	}

	return searchDate;
};

const nextOrderByPattern = (schedule: ApiSchedule): Date | null => {
	const { recurrenceValue, recurrencePattern, weeklyDays, startDate } = schedule;

	if (recurrencePattern === 'daily') {
		return calculateNextOrderDaily(startDate, recurrenceValue);
	}

	if (recurrencePattern === 'weekly' && Boolean(parseWeeklyDays(weeklyDays).length)) {
		return calculateNextOrderWeekly(startDate, parseWeeklyDays(weeklyDays), recurrenceValue);
	}

	if (recurrencePattern === 'monthly') {
		return calculateNextOrderMonthly(schedule);
	}

	return null;
};

export const calculateNextOrderDate = (schedule: ApiSchedule): Date | null => {
	let nextOrderDate = null;

	if (schedule.recurrenceValue) {
		nextOrderDate = nextOrderByPattern(schedule);
	}

	if (nextOrderDate && schedule.endDate && nextOrderDate.getTime() > new Date(schedule.endDate).getTime())
		return null;

	return nextOrderDate;
};
